import { Children, cloneElement, createElement, isValidElement, memo, ReactNode, useContext } from "react";
import { useNavigate } from "react-router";
import { AuthorizationContext } from "./context";
import { useCheckAccess } from "./hooks";
import { AccessDeniedStrategy } from "./types";

export type AuthorizationCheckerProps = {
  children: ReactNode | ReactNode[]
  requiredPermissions?: string | string[]
  requiredOneOfPermissions?: string | string[]
  strategy?: AccessDeniedStrategy,
  disabledProperty?: string
  disabledValue?: any
}

export const AuthorizationChecker = memo<AuthorizationCheckerProps>((
  {
    children,
    requiredPermissions,
    requiredOneOfPermissions,
    strategy = AccessDeniedStrategy.hide,
    disabledProperty = "disable",
    disabledValue = true,
  }) => {
  const { redirectUrl, accessDeniedStub } = useContext(AuthorizationContext);
  const { pending, isAccessAllowed } = useCheckAccess(requiredPermissions, requiredOneOfPermissions);
  const navigate = useNavigate();

  if (pending) {
    return null;
  }

  if (!isAccessAllowed) {
    switch (strategy) {
      case AccessDeniedStrategy.hide :
        return null;
      case AccessDeniedStrategy.disable :
        return <>
          {
            Children.map(
              children,
              child => isValidElement(child) && cloneElement(child, { [disabledProperty]: disabledValue }),
            )
          }
        </>;
      case AccessDeniedStrategy.redirect :
        navigate(redirectUrl);
        return null;
      case AccessDeniedStrategy.renderStub :
        return accessDeniedStub ? (isValidElement(accessDeniedStub) ? accessDeniedStub : createElement(accessDeniedStub as any)) : null;
      default:
        return null;
    }
  }
  return (<>{children}</>);
});

export default AuthorizationChecker;
